import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { catchError, filter, from, map, Observable, of, switchMap, take, timeout } from "rxjs";
import { ArcusAuthService } from "../../core/arcusauth.service";
import UserService from "../services/user.service";
import ExceptionService from "../services/exception.service";

@Injectable({ providedIn: "root" })
export class RoleGuard {
    constructor(
        private authService: ArcusAuthService,
        private userService: UserService,
        private exceptionService: ExceptionService,
        private router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const requiredRoles = route.data["roles"] as string[];

        // Using this guard requires roles to be set, otherwise just remove guard
        if (!requiredRoles || requiredRoles.length === 0) {
            return this.router.parseUrl("/unauthorized");
        }

        return this.userHasRoles(requiredRoles);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    canLoad(route: Route) {
        const requiredRoles = route.data["roles"] as string[];

        // Using this guard requires roles to be set, otherwise just remove guard
        if (!requiredRoles || requiredRoles.length === 0) {
            return this.router.parseUrl("/unauthorized");
        }

        return this.userHasRoles(requiredRoles);
    }

    userHasRoles(roles: string[]): Observable<boolean | UrlTree> {
        return this.authService.isCoreAuthenticated$.pipe(
            timeout(5000),
            take(1),
            switchMap((isLoggedIn) => {
                if (!isLoggedIn) {
                    return of(this.router.parseUrl("/"));
                }

                return this.authService._isAuthenticatedWithAccount$.pipe(
                    filter((hasAccount) => hasAccount),
                    take(1),
                    timeout(5000),
                    switchMap(() =>
                        from(this.userService.currentUser()).pipe(
                            map((user) => {
                                const hasRole = user.Roles?.some((role) => roles.includes(role.RoleName.toUpperCase()));
                                return hasRole ? true : this.router.parseUrl("/unauthorized");
                            }),
                            catchError((err) => {
                                this.exceptionService.handleException(err);
                                return of(this.router.parseUrl("/unauthorized"));
                            })
                        )
                    ),
                    catchError(() => of(this.router.parseUrl("/unauthorized")))
                );
            }),
            catchError(() => of(this.router.parseUrl("/unauthorized")))
        );
    }
}
